import {
  darken,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { ReactElement } from 'react';
import { theme } from '@theguarantors/tg-package-material-ui';

interface Props {
  open: boolean;
  text?: string;
  displayText?: ReactElement;
  action: () => void;
  icon?: JSX.Element;
  ariaLabel?: string;
  selected?: boolean;
  disabled?: boolean;
  outlined?: boolean;
}

const SideMenuItem = ({
  open,
  text,
  displayText,
  action,
  icon,
  ariaLabel,
  selected,
  disabled,
  outlined,
}: Props) => {
  const baseBackgroundColor = theme.palette.coolGrey[50] ?? '#D1D1D1';
  const baseTextColor = theme.palette.text.secondary;
  const disabledTextColor = theme.palette.neutralGrey[700];

  return (
    <ListItem
      disablePadding
      sx={{
        minWidth: '48px',
      }}
    >
      <Tooltip arrow title={text} disableHoverListener={open || !text} placement="right">
        <ListItemButton
          onClick={action}
          className={open ? 'list-item-button-open' : 'list-item-button-close'}
          disabled={disabled}
          sx={{
            outline: outlined ? '1px solid white' : 'none',
            opacity: `${disabled ? '0.5' : '1'} !important`,
            justifyContent: !icon && outlined ? 'center' : 'flex-start',
            alignItems: 'center',
            padding: 0,
            borderRadius: '10px',
            margin: '2px 8px',
            height: '48px',
            textAlign: icon ? 'left' : 'center',
            backgroundColor: `rgba(0,0,0,${selected ? 0.15 : 0})`,
            [':hover']: {
              backgroundColor: darken(baseBackgroundColor, 0.1),
            },
            [':active']: {
              backgroundColor: darken(baseBackgroundColor, 0.2),
            },
          }}
          aria-label={ariaLabel ?? text}
        >
          {icon && (
            <ListItemIcon
              sx={{
                justifyContent: text || !open ? 'center' : 'flex-end',
                alignItems: 'center',
                minWidth: 'unset',
                height: '24px',
                width: '24px',
                margin: '12px',
                ['svg']: {
                  fill: `${disabled ? disabledTextColor : baseTextColor} !important`,
                  fontSize: '22px',
                },
              }}
            >
              {icon}
            </ListItemIcon>
          )}

          {text ? (
            <Collapse
              in={open}
              orientation="horizontal"
              timeout={300}
              sx={{
                margin: '12px',
                flex: 1,
                alignItems: 'center',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                transition: 'all 0.3s linear',
              }}
            >
              <ListItemText
                primary={displayText ?? text}
                primaryTypographyProps={{
                  color: disabled ? disabledTextColor : baseTextColor,
                  sx: {
                    fontSize: '16px !important',
                  },
                }}
                sx={{
                  margin: 0,
                  lineHeight: '24px !important',
                  height: '24px !important',
                }}
              />
            </Collapse>
          ) : null}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
};

export default SideMenuItem;
